import React, { createContext, useContext, useState, useEffect } from 'react';
// import { doc, getDoc, setDoc } from 'firebase-shim';
// import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import indexedDBService from '../utils/indexedDBService';

export type WidgetName =
  | 'currentMonthInsights'
  | 'profitTrend'
  | 'bankSummary'
  | 'purchaseInsights'
  | 'salesExpenses'
  | 'outstandingInvoices'
  | 'clientReceivables'
  | 'financialDistribution';

interface WidgetSettings {
  [key: string]: boolean;
}

interface WidgetSettingsContextProps {
  widgetSettings: WidgetSettings;
  isWidgetVisible: (widgetName: WidgetName) => boolean;
  toggleWidgetVisibility: (widgetName: WidgetName) => Promise<void>;
  loading: boolean;
}

const defaultWidgetSettings: WidgetSettings = {
  currentMonthInsights: true, // Current Month Insights widget is visible by default
  profitTrend: true,          // Profit Trend widget is visible by default
  bankSummary: true,          // Bank Summary widget is visible by default
  purchaseInsights: true,     // Purchase Insights widget is visible by default
  salesExpenses: true,        // Sales vs Expenses widget is visible by default
  outstandingInvoices: true,  // Outstanding Invoices widget is visible by default
  clientReceivables: true,    // Client Receivables widget is visible by default
  financialDistribution: true // Financial Distribution widget is visible by default
};

const WidgetSettingsContext = createContext<WidgetSettingsContextProps | undefined>(undefined);

export const WidgetSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [widgetSettings, setWidgetSettings] = useState<WidgetSettings>(defaultWidgetSettings);
  const [loading, setLoading] = useState(true);

  const fetchWidgetSettings = async () => {
    if (!currentUser) {
      setWidgetSettings(defaultWidgetSettings);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Use IndexedDB instead of Firebase
      const settings = await indexedDBService.getItemById<any>('userSettings', currentUser.uid);

      if (settings && settings.widgets) {
        setWidgetSettings(settings.widgets);
      } else {
        // If no settings exist yet, use defaults and save them
        await indexedDBService.putItem('userSettings', {
          id: currentUser.uid,
          widgets: defaultWidgetSettings,
          userId: currentUser.uid
        });
        setWidgetSettings(defaultWidgetSettings);
      }
    } catch (error) {
      console.error('Error fetching widget settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWidgetSettings();
  }, [currentUser]);

  const isWidgetVisible = (widgetName: WidgetName): boolean => {
    return widgetSettings[widgetName] || false;
  };

  const toggleWidgetVisibility = async (widgetName: WidgetName): Promise<void> => {
    if (!currentUser) return;

    try {
      const newSettings = {
        ...widgetSettings,
        [widgetName]: !widgetSettings[widgetName]
      };

      // Update local state
      setWidgetSettings(newSettings);

      // Save to IndexedDB
      // We need to merge with existing settings
      const existingSettings = await indexedDBService.getItemById<any>('userSettings', currentUser.uid) || { id: currentUser.uid, userId: currentUser.uid };

      await indexedDBService.putItem('userSettings', {
        ...existingSettings,
        widgets: newSettings
      });

    } catch (error) {
      console.error('Error updating widget settings:', error);
      // Revert local state if save fails
      setWidgetSettings(widgetSettings);
    }
  };

  return (
    <WidgetSettingsContext.Provider
      value={{
        widgetSettings,
        isWidgetVisible,
        toggleWidgetVisibility,
        loading
      }}
    >
      {children}
    </WidgetSettingsContext.Provider>
  );
};

export const useWidgetSettings = (): WidgetSettingsContextProps => {
  const context = useContext(WidgetSettingsContext);
  if (context === undefined) {
    throw new Error('useWidgetSettings must be used within a WidgetSettingsProvider');
  }
  return context;
}; 
